import React, { useCallback, useEffect, useState } from 'react';
import Icon from '@ant-design/icons';
import EventCacheWidgetProcessor, { EventCacheWidgetState, getDefaultState } from '@kemu-io/kemu-core/widgets/eventCache/index.js';
import { WidgetPortContext } from '@kemu-io/kemu-core/types';
import { GetPortsInformationFunction, GateUI, GateUIProps } from '../index';
import GateIcon from '../../gateIcon/gateIcon';
import { ReactComponent as EventCacheWidgetIcon } from './icon.svg';
import { PortLocation } from '../../../types/canvas_t';
import styles from './eventCache.module.css';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import useTranslation from '@hooks/useTranslation';
import TextInput from '@components/WidgetsComponents/TextInput/TextInput';
import { useDebouncedCallback } from 'use-debounce';

const EventCacheWidget = (props: GateUIProps): React.JSX.Element => {
	const { recipeId, thingRecipeId, info } = props;
	const [state, setState] = useReactiveWidgetState<EventCacheWidgetState>(recipeId, thingRecipeId, info.id);
	const t = useTranslation('LogicMapper.Widgets.EventCache');
  
	const fixedState = {
    ...getDefaultState(),
		...state,
	};
  
  const [localName, setLocalName] = useState(fixedState.scopeName || 'default');
  const updateStateName = useDebouncedCallback((name: string) => {
		setState((s) => ({
			...s,
			scopeName: name,
		}));
	}, 300);

	const updateScopeName = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
		const name = evt.currentTarget.value;
    setLocalName(name);
	}, []);
 
  // Update the widget's internal state after user stops typing
  useEffect(() => {
    updateStateName(localName);
  }, [localName]);

	return (
		<div className={styles.InputWrapper}>
			<TextInput
				value={localName}
				placeholder={t('ScopeNamePlaceholder', 'Example: user')}
				onChange={updateScopeName}
			/>
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={EventCacheWidgetIcon} />}/>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = EventCacheWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = EventCacheWidgetProcessor.getInputNames(state, portContext);

	const positions: Record<string, PortLocation> = {
		[inputNames[0].name]: [0, 0.20, -1, 0],
		[inputNames[1].name]: [0, 0.5, -1, 0],
		[inputNames[2].name]: [0, 0.80, -1, 0],
	};

	const outputPositions: Record<string, PortLocation> = {
		[outputNames[0].name]: [1, 0.32, 1, 0],
		[outputNames[1].name]: [1, 0.68, 1, 0],
	};

	return {
		inputs: inputNames.map(input => ({
			name: input.name,
			type: input.type,
			position: positions[input.name] || 'Left',
		})),

		outputs: outputNames.map(output => ({
			name: output.name,
			type: output.type,
			position: outputPositions[output.name] || [1, 0.5, 1, 0] as PortLocation,
		})),
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: EventCacheWidget,
	hasTitle: true,
	getWidgetTitle: (intl) => intl.formatMessage({ defaultMessage: 'Event Cache', id: 'LogicMapper.Widgets.EventCache.Title' }),
} as GateUI;

